import React from 'react';

import { useGraphQLQuery } from '@betterment/graphql';
import { Box, Card, Dialog, H2, HStack, Icon, Text, VStack } from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { CardLoader, SambaAutoLink, useFeatureFlag } from '@betterment/shared-components';
import { useColorMode } from '@betterment/shared-components/src/components/color-mode/ColorModeProvider';

import { ProductOnboardingCrossSellItem } from './components/ProductOnboardingCrossSellItem';
import { SecuritiesLendingCrossSellItem } from './components/SecuritiesLendingCrossSellItem';
import { useShouldShowSecurityLending } from './hooks/useShouldShowSecurityLending';

import { trackClick } from '../../features/track';
import { ProductCategoryOptionsOrder } from '../../graphql/schema';
import { addAccountUrl } from '../../Shared/addAccountUrl';
import { urlBuilder } from '../../Shared/urlBuilder';
import { AccountRecommendationModal } from '../account-recommendation-modal/AccountRecommendationModal';
import { RoundedBorderBox } from '../account-recommendation-modal/components/RoundedBorderBox';
import { AccountRecommendationOptionDocument } from '../account-recommendation-modal/graphql/query.accountRecommendationOption.generated';
import { useProductOnboardingOptions } from '../hooks/useProductOnboardingOptions';

type CrossSellOption = {
  id: string;
  name: string;
  yieldDescription?: string | null;
  image: Record<string, string>;
  legacyProductCategory: string;
};
type SecLendingOption = { type: 'secLending' };
type DisplayOption = CrossSellOption | SecLendingOption;

const isSecLendingOption = (item: DisplayOption): item is SecLendingOption => {
  return 'type' in item && item.type === 'secLending';
};

const isDirectTradingOption = (item: CrossSellOption) => {
  return item.legacyProductCategory === 'direct_trading';
};

const buildDisplayOptions = (options: CrossSellOption[], shouldShowSecLending: boolean): DisplayOption[] => {
  if (!shouldShowSecLending) {
    return options.slice(0, 4);
  }

  return [options[0], { type: 'secLending' as const }, ...options.slice(1, 3)];
};

export const ProductOnboardingCrossSellCard: React.FC = () => {
  const { data } = useProductOnboardingOptions({ order: ProductCategoryOptionsOrder.CrossSell });
  const { colorMode } = useColorMode();
  const { enabled: isAccountRecommendationEnabled } = useFeatureFlag({
    splitName: 'retail.ai_account_recommendation_q1_2026_enabled',
    context: 'ProductOnboardingCrossSellCard',
  });
  const { enabled: isSdiJointAndTrustsEnabled } = useFeatureFlag({
    splitName: 'retail.sdi_joint_and_trusts_flow_q1_2026_enabled',
    context: 'ProductOnboardingCrossSellCard',
  });
  const { data: accountRecommendationOptionData } = useGraphQLQuery(AccountRecommendationOptionDocument, {
    throwOnError: false,
    skip: !isAccountRecommendationEnabled,
  });

  const shouldShowSecLending = useShouldShowSecurityLending();

  if (!data) return <CardLoader />;

  if (data.crossSellOptions.length === 0) return null;

  const options = data.crossSellOptions.slice(0, 4);
  const { accountRecommendationOption } = accountRecommendationOptionData || {};

  const displayOptions = buildDisplayOptions(options, shouldShowSecLending);

  return (
    <Card data-testid="specPageProductCrossSellCard">
      <Card.Header css={{ paddingX: 'space.300', paddingTop: 'space.300' }}>
        <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
          <H2 color="text" variant="title200">
            Explore more
          </H2>
          <SambaAutoLink
            to={urlBuilder.addGoal.absolute()}
            onClick={() => trackClick({ name: 'explore_more_products_see_all' })}
          >
            See all
          </SambaAutoLink>
        </Box>
      </Card.Header>
      <Card.Content css={{ padding: 'space.300' }}>
        <VStack css={{ gap: 'space.200' }}>
          <Box
            css={{
              display: 'grid',
              gridTemplateColumns: {
                base: '1fr 1fr',
                xl: '1fr 1fr 1fr 1fr',
              },
              gap: 'space.100',
            }}
          >
            {displayOptions.map(item => {
              if (isSecLendingOption(item)) {
                return <SecuritiesLendingCrossSellItem key="sec-lending" />;
              }

              let url = addAccountUrl({ product_category: item.legacyProductCategory });
              if (isDirectTradingOption(item) && isSdiJointAndTrustsEnabled) {
                url = urlBuilder.appAddAccountDirectTradingIntroduction.absolute();
              }

              return (
                <ProductOnboardingCrossSellItem
                  key={item.id}
                  id={item.id}
                  name={item.name}
                  description={item.yieldDescription || ''}
                  icon={
                    <img
                      src={item.image[colorMode]}
                      alt={`${item.name} icon`}
                      className={css({ width: '36px', height: '36px' })}
                    />
                  }
                  to={url}
                  analyticsKey={item.name.toLowerCase().replace(/\s/g, '_')}
                />
              );
            })}
          </Box>
          {accountRecommendationOption && (
            <AccountRecommendationModal>
              <Dialog.Trigger asChild={true}>
                <RoundedBorderBox
                  borderRadius={20}
                  borderStyle="standard"
                  role="button"
                  tabIndex={0}
                  style={{ width: '100%' }}
                  onClick={() =>
                    trackClick({
                      name: 'GetAccountRecommendation',
                      module: 'ProductCatalog.products',
                    })
                  }
                >
                  <HStack
                    css={{
                      px: 'space.100',
                      py: 'space.100',
                      gap: 'space.50',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon name="Guidance" color="icon.accent.gold" size="xsmall" />
                    <Text variant="text50" color="text" css={{ fontWeight: 'medium' }}>
                      Get an account recommendation
                    </Text>
                  </HStack>
                </RoundedBorderBox>
              </Dialog.Trigger>
            </AccountRecommendationModal>
          )}
        </VStack>
      </Card.Content>
    </Card>
  );
};
